import { useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";

import type { Deal } from "../types";
import { computeReplyClock } from "./replyClock";
import { usePublicHolidays } from "./usePublicHolidays";

/**
 * Badge showing the 48-working-hour first-reply clock of an enquiry.
 * Neutral while running, amber when due soon, red "Overdue" once past the
 * deadline. Renders nothing without a received date, after a first reply, or
 * for closed (won / lost) enquiries. The state is always stated in text.
 */
export const ReplyClockBadge = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  const { holidays, isPending } = usePublicHolidays();
  if (isPending) return null;

  const { state, hoursLeft } = computeReplyClock(deal, holidays, new Date());
  if (state === "overdue") {
    return (
      <Badge variant="destructive">
        {translate("resources.deals.reply_clock.overdue")}
      </Badge>
    );
  }
  if (state !== "running" && state !== "due-soon") return null;

  const hours = Math.max(1, Math.ceil(hoursLeft ?? 0));
  const isDueSoon = state === "due-soon";
  return (
    <Badge
      variant="outline"
      className={
        isDueSoon
          ? "border-amber-500 bg-amber-100 text-amber-900 dark:border-amber-400 dark:bg-amber-950 dark:text-amber-200"
          : undefined
      }
    >
      {translate(
        isDueSoon
          ? "resources.deals.reply_clock.due_soon"
          : "resources.deals.reply_clock.running",
        { hours },
      )}
    </Badge>
  );
};
